import {tokensForThemeProvider} from '@forecast-it/design-system';
import styled from 'styled-components';

/**
 * The reply composer's input, drawn in the comment composer's own type.
 *
 * The comment box beside this one is the design system's editor, and that editor's text is set by
 * the wrapper it is mounted in, from the body token of whichever theme is live - body 1, measured
 * on the running app as 16px/24px. The editor ships a utility class that would take it to 14px,
 * but that class does not win in this app, so the token is what actually renders and the token is
 * therefore what this has to be. The themes do not agree on a face either, so a stack copied in
 * here would be right on one of them and wrong on the other.
 *
 * Read from the same token instead, colour included: the two modes of one control set the same
 * words in the same type, whatever the theme is.
 *
 * The box itself is laid out by the stylesheet that draws the frame around it; only the type and
 * what has to sit with it live here.
 */
// The same guard the other pieces of this thread carry: these are rendered in tests with no theme
// mounted, and in the app every one of them sits under the modal conductor's provider. Falling back
// to the default theme's own tokens keeps a bare render honest rather than blank.
const FALLBACK_BODY_TYPE = tokensForThemeProvider?.sky?.sys?.font?.body['1'] ?? '';
const FALLBACK_TEXT_COLOUR = tokensForThemeProvider?.sky?.sys?.color?.text.content.main ?? '';

export const NovaReplyTextarea = styled.textarea`
	box-sizing: border-box;
	display: block;
	width: 100%;
	height: 100%;
	margin: 0;
	// Not 0: the editor insets its text twice, 8px on the scrolling area and 4px more on the
	// paragraph inside it, and only the first of those is on the frame here. Without the second the
	// same words sit 4px higher than they do in the other mode, and the text jumps on the way
	// between them.
	padding: 4px 0;
	border: 0;
	background: transparent;
	${props => props.theme?.sys?.font?.body['1'] ?? FALLBACK_BODY_TYPE}
	color: ${props => props.theme?.sys?.color?.text.content.main ?? FALLBACK_TEXT_COLOUR};
	// The comment box does not grow a drag handle, so neither does this one; it scrolls once the
	// text outgrows the box instead.
	resize: none;
	overflow-y: auto;

	&::placeholder {
		// The editor dims its own placeholder with this variable, and it is the same stylesheet that
		// gives the frame around this field its border and background.
		color: hsl(var(--muted-foreground));
	}

	&:focus {
		// Matches the editor's own focus treatment: the frame stays put, and the browser's default
		// ring is what would otherwise change the box's size on focus.
		outline: none;
	}
`;
